import * as React from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import { Input } from "./input";
import { textIncludesSearch } from "@/lib/searchText";
import { TABLE_SEARCH_INPUT_CLASS } from "./table-search-input";

export interface AutocompleteOption {
  value: string | number;
  label: string;
  meta?: string;
}

interface AutocompleteProps {
  options: AutocompleteOption[];
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (option: AutocompleteOption) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  required?: boolean;
  /** Se true, não lista opções com o campo vazio (só sugere após digitar). */
  requireQueryForSuggestions?: boolean;
  /** Ícone de lupa à esquerda (padrão tabelas). */
  showSearchIcon?: boolean;
  /** Botão X para limpar quando houver texto. */
  showClearButton?: boolean;
  /**
   * `overlay`: lista flutuante (padrão).
   * `inline`: lista abaixo do campo, empurra o layout para baixo (modais).
   */
  listLayout?: "overlay" | "inline";
  /** Máximo de itens visíveis na lista; excedente rola só dentro da lista. */
  maxVisibleItems?: number;
  /** Renderiza a lista no document.body para não sofrer clipping de tabelas/modais. */
  portal?: boolean;
}

/** Altura aproximada de uma linha (label + meta opcional). */
const AUTOCOMPLETE_ROW_HEIGHT_PX = 52;

export function Autocomplete({
  options,
  value,
  onValueChange,
  onSelect,
  placeholder,
  className,
  id,
  required,
  requireQueryForSuggestions = false,
  showSearchIcon = false,
  showClearButton = false,
  listLayout = "overlay",
  maxVisibleItems,
  portal = false,
}: AutocompleteProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [filteredOptions, setFilteredOptions] = React.useState<AutocompleteOption[]>([]);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [portalStyle, setPortalStyle] = React.useState<React.CSSProperties>({});
  const showClear = showClearButton && value.length > 0;
  const listMaxHeight =
    maxVisibleItems != null && maxVisibleItems > 0
      ? maxVisibleItems * AUTOCOMPLETE_ROW_HEIGHT_PX
      : listLayout === "overlay"
        ? 240
        : undefined;

  React.useEffect(() => {
    if (requireQueryForSuggestions && value.trim() === "") {
      setFilteredOptions([]);
      return;
    }
    if (value === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((opt) => textIncludesSearch(opt.label, value));
      setFilteredOptions(filtered);
    }
  }, [value, options, requireQueryForSuggestions]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        !listRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const updatePortalPosition = React.useCallback(() => {
    if (!portal || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const maxHeight = listMaxHeight ?? 240;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openAbove = spaceBelow < Math.min(maxHeight, 160) && rect.top > spaceBelow;
    setPortalStyle({
      position: "fixed",
      left: rect.left,
      top: openAbove ? undefined : rect.bottom + 4,
      bottom: openAbove ? window.innerHeight - rect.top + 4 : undefined,
      width: rect.width,
      maxHeight,
      zIndex: 300,
    });
  }, [portal, listMaxHeight]);

  React.useEffect(() => {
    if (!isOpen || !portal) return;
    updatePortalPosition();
    window.addEventListener("resize", updatePortalPosition);
    window.addEventListener("scroll", updatePortalPosition, true);
    return () => {
      window.removeEventListener("resize", updatePortalPosition);
      window.removeEventListener("scroll", updatePortalPosition, true);
    };
  }, [isOpen, portal, updatePortalPosition]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange(e.target.value);
    const next = e.target.value;
    if (requireQueryForSuggestions && next.trim() === "") {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  const handleOptionClick = (option: AutocompleteOption) => {
    onValueChange(option.label);
    onSelect(option);
    setIsOpen(false);
  };

  const handleClear = () => {
    onValueChange("");
    inputRef.current?.focus();
    if (requireQueryForSuggestions) {
      setIsOpen(false);
    }
  };

  const inputClassName = `${className ?? TABLE_SEARCH_INPUT_CLASS}${showClear ? " pr-9" : ""}`;

  const listClassName =
    listLayout === "inline"
      ? "mt-1 w-full overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white shadow-lg"
      : portal
        ? "overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white shadow-lg"
        : "absolute z-50 mt-1 w-full overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white shadow-lg";

  const optionsList =
    isOpen && filteredOptions.length > 0 ? (
      <div
        ref={listRef}
        className={listClassName}
        style={portal ? portalStyle : listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
      >
        {filteredOptions.map((option, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleOptionClick(option)}
            className="w-full border-b border-gray-100 px-4 py-2 text-left last:border-b-0 hover:bg-accent/10 focus:bg-accent/10 focus:outline-none"
          >
            <div className="font-medium text-foreground">{option.label}</div>
            {option.meta && <div className="text-xs text-muted-foreground">{option.meta}</div>}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div ref={wrapperRef} className="relative w-full">
      {showSearchIcon ? (
        <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      ) : null}
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={() => {
          if (requireQueryForSuggestions && value.trim() === "") {
            setIsOpen(false);
            return;
          }
          updatePortalPosition();
          setIsOpen(true);
        }}
        placeholder={placeholder}
        className={inputClassName}
        required={required}
        autoComplete="off"
      />
      {showClear ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 z-10 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-[#000] hover:opacity-70"
          aria-label="Limpar pesquisa"
          tabIndex={-1}
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      ) : null}
      {portal && typeof document !== "undefined"
        ? optionsList
          ? createPortal(optionsList, document.body)
          : null
        : optionsList}
    </div>
  );
}
